import {
  ALL_BODY_GENES, ALL_EAR_GENES, ALL_EGG_PATTERNS, ALL_HORN_GENES, ALL_MARKING_GENES, ALL_TAIL_GENES,
  type AlchemyType, type EvolutionTrait, type Genome, type RecessiveGenes,
} from './types';

type Rng = () => number;

const clampHue = (h: number): number => ((Math.round(h) % 360) + 360) % 360;

/**
 * Média circular de dois hues (0..359) com mutação ±15°.
 * Usa atan2 sobre vetores unitários para evitar wrap-around incorreto.
 */
export function blendHue(a: number, b: number, rng: Rng = Math.random): number {
  const aRad = (a * Math.PI) / 180;
  const bRad = (b * Math.PI) / 180;
  const x = Math.cos(aRad) + Math.cos(bRad);
  const y = Math.sin(aRad) + Math.sin(bRad);
  const avg = (Math.atan2(y, x) * 180) / Math.PI;
  const mutation = (rng() - 0.5) * 30;
  return clampHue(avg + mutation);
}

/**
 * Combina tipos elementais dos pais.
 * - União com poda probabilística:
 *   - Tipo presente em ambos pais → 100% inclusão.
 *   - Tipo presente em apenas um → 50% inclusão.
 * - Garante mínimo 1 tipo (sorteia da união se vazio).
 * - Cap em 3 tipos.
 */
export function breedTypes(
  a: ReadonlyArray<AlchemyType>,
  b: ReadonlyArray<AlchemyType>,
  rng: Rng = Math.random,
): AlchemyType[] {
  const setA = new Set(a);
  const setB = new Set(b);
  const union = [...new Set([...a, ...b])];
  const kept = union.filter((t) => {
    const inBoth = setA.has(t) && setB.has(t);
    return inBoth || rng() < 0.5;
  });
  if (kept.length === 0) {
    if (union.length === 0) return ['Fire'];
    const idx = Math.min(Math.floor(rng() * union.length), union.length - 1);
    return [union[idx]!];
  }
  return kept.slice(0, 3);
}

/**
 * Herança de traits por rarity:
 * - common: 70%; rare: 50%; epic: 30%.
 * - Combina pais (dedup por id) e cap em 5.
 */
export function inheritTraits(
  a: ReadonlyArray<EvolutionTrait>,
  b: ReadonlyArray<EvolutionTrait>,
  rng: Rng = Math.random,
): EvolutionTrait[] {
  const seen = new Map<string, EvolutionTrait>();
  for (const t of [...a, ...b]) {
    if (!seen.has(t.id)) seen.set(t.id, t);
  }
  const result: EvolutionTrait[] = [];
  for (const t of seen.values()) {
    const chance = t.rarity === 'common' ? 0.7 : t.rarity === 'rare' ? 0.5 : 0.3;
    if (rng() < chance) result.push(t);
    if (result.length >= 5) break;
  }
  return result;
}

const clamp01to100 = (v: number): number => Math.min(100, Math.max(0, v));

/**
 * Herança fraca de predisposição corporal: média dos placares finais de
 * cuidado dos dois pais (não um "pick" 50/50 como os outros genes), com
 * mutação leve ±10 — mesma família de comportamento de `blendHue`.
 */
export function inheritBodyPredisposition(scoreA: number, scoreB: number, rng: Rng = Math.random): number {
  const avg = (scoreA + scoreB) / 2;
  const mutation = (rng() - 0.5) * 20; // -10..+10
  return clamp01to100(Math.round(avg + mutation));
}

export interface BreedInput {
  parentA: Genome;
  parentB: Genome;
  /** careFinalScore dos pais (Lifecycle/NpcCreature), 50 se nunca evoluiu (ex.: estranho aleatório). */
  parentACareScore?: number;
  parentBCareScore?: number;
  rng?: Rng;
  mutationChance?: number;
}

export interface BreedResult {
  genome: Genome;
  inheritance: InheritanceReport;
}

export type InheritanceOrigin = 'parent-a' | 'parent-b' | 'both' | 'variation';
export interface InheritanceReport {
  body: InheritanceOrigin;
  ears: InheritanceOrigin;
  horns: InheritanceOrigin;
  tail: InheritanceOrigin;
  markings: InheritanceOrigin;
  eggPattern: InheritanceOrigin;
  paletteHue: InheritanceOrigin;
  primaryTypes: InheritanceOrigin;
  inheritedTraits: InheritanceOrigin;
}

/** Cruza dois genomas. Modifiers ficam vazios em U2; U5 popula via mutações. */
export function breedGenome(input: BreedInput): BreedResult {
  const rng = input.rng ?? Math.random;
  const pick = <K extends keyof Genome>(k: K): Genome[K] =>
    rng() < 0.5 ? input.parentA[k] : input.parentB[k];

  const inheritedTraits = inheritTraits(
    input.parentA.inheritedTraits,
    input.parentB.inheritedTraits,
    rng,
  );

  const inheritAllele = <T>(visibleA: T, hiddenA: T | undefined, visibleB: T, hiddenB: T | undefined): [T, T] => {
    if (hiddenA === undefined && hiddenB === undefined) {
      return rng() < 0.5 ? [visibleA, visibleB] : [visibleB, visibleA];
    }
    const transmittedA = hiddenA !== undefined && rng() >= 0.5 ? hiddenA : visibleA;
    const transmittedB = hiddenB !== undefined && rng() >= 0.5 ? hiddenB : visibleB;
    return rng() < 0.5 ? [transmittedA, transmittedB] : [transmittedB, transmittedA];
  };
  const recessiveA = input.parentA.recessiveGenes ?? {};
  const recessiveB = input.parentB.recessiveGenes ?? {};
  const [body, hiddenBody] = inheritAllele(input.parentA.body, recessiveA.body, input.parentB.body, recessiveB.body);
  const [ears, hiddenEars] = inheritAllele(input.parentA.ears, recessiveA.ears, input.parentB.ears, recessiveB.ears);
  const [horns, hiddenHorns] = inheritAllele(input.parentA.horns, recessiveA.horns, input.parentB.horns, recessiveB.horns);
  const [tail, hiddenTail] = inheritAllele(input.parentA.tail, recessiveA.tail, input.parentB.tail, recessiveB.tail);
  const [markings, hiddenMarkings] = inheritAllele(input.parentA.markings, recessiveA.markings, input.parentB.markings, recessiveB.markings);
  const [eggPattern, hiddenEggPattern] = inheritAllele(input.parentA.eggPattern, recessiveA.eggPattern, input.parentB.eggPattern, recessiveB.eggPattern);
  const recessiveGenes: RecessiveGenes = {
    body: hiddenBody, ears: hiddenEars, horns: hiddenHorns, tail: hiddenTail,
    markings: hiddenMarkings, eggPattern: hiddenEggPattern,
  };

  const genome: Genome = {
    body,
    eyes: pick('eyes'),
    mouth: pick('mouth'),
    ears,
    horns,
    tail,
    markings,
    paletteHue: blendHue(input.parentA.paletteHue, input.parentB.paletteHue, rng),
    bodyPredisposition: inheritBodyPredisposition(
      input.parentACareScore ?? 50,
      input.parentBCareScore ?? 50,
      rng,
    ),
    eggPattern,
    primaryTypes: breedTypes(input.parentA.primaryTypes, input.parentB.primaryTypes, rng),
    modifiers: [],
    inheritedTraits,
    recessiveGenes,
  };

  const mutationChance = Math.max(0, Math.min(1, input.mutationChance ?? 0.03));
  if (rng() >= 1 - mutationChance) {
    const loci = [
      ['body', ALL_BODY_GENES], ['ears', ALL_EAR_GENES], ['horns', ALL_HORN_GENES],
      ['tail', ALL_TAIL_GENES], ['markings', ALL_MARKING_GENES], ['eggPattern', ALL_EGG_PATTERNS],
    ] as const;
    const locusIndex = Math.min(Math.floor((1 - rng()) * loci.length), loci.length - 1);
    const [locus, catalog] = loci[locusIndex]!;
    const alternatives = catalog.filter((allele) => allele !== genome[locus]) as Array<Genome[typeof locus]>;
    const alleleIndex = Math.min(Math.floor(rng() * alternatives.length), alternatives.length - 1);
    const mutated = alternatives[alleleIndex]!;
    (genome as unknown as Record<string, unknown>)[locus] = mutated;
    genome.modifiers.push({ id: `rare-${locus}-${String(mutated)}` });
  }
  const discreteOrigin = <T>(value: T, a: T, b: T): InheritanceOrigin => {
    if (a === b && value === a) return 'both';
    if (value === a) return 'parent-a';
    if (value === b) return 'parent-b';
    return 'variation';
  };
  const listOrigin = <T>(value: ReadonlyArray<T>, a: ReadonlyArray<T>, b: ReadonlyArray<T>): InheritanceOrigin => {
    const signature = (items: ReadonlyArray<T>): string => [...items].map(String).sort().join('|');
    const result = signature(value);
    const fromA = result === signature(a);
    const fromB = result === signature(b);
    if (fromA && fromB) return 'both';
    if (fromA) return 'parent-a';
    if (fromB) return 'parent-b';
    return 'both';
  };
  const inheritance: InheritanceReport = {
    body: discreteOrigin(genome.body, input.parentA.body, input.parentB.body),
    ears: discreteOrigin(genome.ears, input.parentA.ears, input.parentB.ears),
    horns: discreteOrigin(genome.horns, input.parentA.horns, input.parentB.horns),
    tail: discreteOrigin(genome.tail, input.parentA.tail, input.parentB.tail),
    markings: discreteOrigin(genome.markings, input.parentA.markings, input.parentB.markings),
    eggPattern: discreteOrigin(genome.eggPattern, input.parentA.eggPattern, input.parentB.eggPattern),
    paletteHue: 'both',
    primaryTypes: listOrigin(genome.primaryTypes, input.parentA.primaryTypes, input.parentB.primaryTypes),
    inheritedTraits: listOrigin(
      genome.inheritedTraits.map((trait) => trait.id),
      input.parentA.inheritedTraits.map((trait) => trait.id),
      input.parentB.inheritedTraits.map((trait) => trait.id),
    ),
  };
  return { genome, inheritance };
}
